import React, { useState, useRef } from 'react';
import { Box, Typography, CircularProgress, LinearProgress, IconButton, useTheme, alpha } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { MediaService } from '../api/services/mediaService';
import { toast } from '../utils/toast';

export default function FileUploadDropzone({ 
    onUploadSuccess, 
    onMultipleUploadSuccess,
    accept = "image/*,application/pdf", 
    maxSizeMB = 5,
    multiple = false
}) {
    const theme = useTheme();
    const fileInputRef = useRef(null);
    const [isDragActive, setIsDragActive] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStatusText, setUploadStatusText] = useState('');

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isUploading) setIsDragActive(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
        
        if (isUploading) return;

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const files = multiple 
                ? Array.from(e.dataTransfer.files) 
                : [e.dataTransfer.files[0]];
            await handleFilesProcess(files);
        }
    };

    const handleFileChange = async (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const files = multiple 
                ? Array.from(e.target.files) 
                : [e.target.files[0]];
            await handleFilesProcess(files);
        }
        // Reset input value so same file can be selected again if needed
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const validateSingleFile = async (file) => {
        if (accept) {
            const acceptedTypes = accept.split(',').map(t => t.trim());
            
            // Step 1: Check extension/browser MIME type first
            const fileTypeValid = acceptedTypes.some(type => {
                if (type.startsWith('.')) {
                    return file.name.toLowerCase().endsWith(type.toLowerCase());
                }
                if (type.includes('/*')) {
                    const category = type.split('/')[0];
                    return file.type.startsWith(category + '/');
                }
                return file.type === type;
            });

            if (!fileTypeValid) {
                toast.error(`Invalid file format for "${file.name}". Allowed types: PNG/JPG/PDF.`);
                return false;
            }

            // Step 2: Validate the magic numbers / actual file signature
            const signatureValid = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = (e) => {
                    if (e.target.readyState !== FileReader.DONE) {
                        resolve(false);
                        return;
                    }
                    const arr = new Uint8Array(e.target.result);
                    if (arr.length < 2) {
                        resolve(false);
                        return;
                    }

                    let header = "";
                    for (let i = 0; i < Math.min(arr.length, 12); i++) {
                        header += arr[i].toString(16).padStart(2, '0').toUpperCase();
                    }

                    // Check for executable header (MZ - 4D 5A)
                    if (header.startsWith("4D5A")) {
                        resolve(false);
                        return;
                    }

                    let detectedType = null;
                    if (header.startsWith("89504E47")) {
                        detectedType = "image/png";
                    } else if (header.startsWith("FFD8FF")) {
                        detectedType = "image/jpeg";
                    } else if (header.startsWith("47494638")) {
                        detectedType = "image/gif";
                    } else if (header.startsWith("25504446")) {
                        detectedType = "application/pdf";
                    } else if (header.startsWith("52494646") && header.includes("57454250")) {
                        detectedType = "image/webp";
                    }

                    if (!detectedType) {
                        const expectsImage = acceptedTypes.some(type => type.startsWith('image/') || type.startsWith('.'));
                        if (expectsImage) {
                            resolve(false);
                            return;
                        }
                        resolve(true);
                        return;
                    }

                    const isValid = acceptedTypes.some(type => {
                        if (type.startsWith('.')) {
                            return file.name.toLowerCase().endsWith(type.toLowerCase());
                        }
                        if (type.includes('/*')) {
                            const category = type.split('/')[0];
                            return detectedType.startsWith(category + '/');
                        }
                        return detectedType === type;
                    });
                    resolve(isValid);
                };
                reader.readAsArrayBuffer(file.slice(0, 12));
            });

            if (!signatureValid) {
                toast.error(`File signature invalid for "${file.name}". Allowed types: PNG/JPG/PDF.`);
                return false;
            }
        }

        // Validate size
        if (file.size > maxSizeMB * 1024 * 1024) {
            toast.error(`File "${file.name}" is too large. Maximum size is ${maxSizeMB}MB.`);
            return false;
        }

        return true;
    };

    const handleFilesProcess = async (files) => {
        if (!files || files.length === 0) return;

        // Validate all files
        const validFiles = [];
        for (const file of files) {
            const isValid = await validateSingleFile(file);
            if (isValid) {
                validFiles.push(file);
            }
        }

        if (validFiles.length === 0) return;

        setIsUploading(true);
        setUploadProgress(0);

        const uploadedUrls = [];
        try {
            for (let i = 0; i < validFiles.length; i++) {
                const currentFile = validFiles[i];
                if (validFiles.length > 1) {
                    setUploadStatusText(`Uploading ${i + 1} of ${validFiles.length}: ${currentFile.name}`);
                } else {
                    setUploadStatusText(`Uploading ${currentFile.name}...`);
                }

                const response = await MediaService.uploadFile(currentFile, (percent) => {
                    if (validFiles.length === 1) {
                        setUploadProgress(percent);
                    } else {
                        const batchProgress = Math.round(((i * 100) + percent) / validFiles.length);
                        setUploadProgress(batchProgress);
                    }
                });

                if (response?.url) {
                    uploadedUrls.push(response.url);
                    if (onUploadSuccess && !onMultipleUploadSuccess) {
                        onUploadSuccess(response.url);
                    }
                }
            }

            if (onMultipleUploadSuccess && uploadedUrls.length > 0) {
                onMultipleUploadSuccess(uploadedUrls);
            }

            toast.success(uploadedUrls.length > 1 ? `${uploadedUrls.length} files successfully uploaded` : "File successfully uploaded to cloud storage");
        } catch (error) {
            toast.error(error.message || "Upload failed. Please try again.");
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
            setUploadStatusText('');
        }
    };

    return (
        <Box
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !isUploading && fileInputRef.current?.click()}
            sx={{
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : 'divider',
                borderRadius: 3,
                bgcolor: isDragActive ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isUploading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    borderColor: !isUploading && 'primary.main',
                    bgcolor: !isUploading && alpha(theme.palette.primary.main, 0.02)
                },
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept={accept}
                multiple={multiple}
                disabled={isUploading}
            />

            {isUploading ? (
                <Box sx={{ width: '100%', textAlign: 'center' }}>
                    <CircularProgress size={44} thickness={4} sx={{ mb: 1.5 }} />
                    <Typography variant="body2" fontWeight="bold" color="text.primary">
                        {uploadStatusText || "Uploading..."}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
                        <Box sx={{ width: '100%', mr: 1 }}>
                            <LinearProgress 
                                variant="determinate" 
                                value={uploadProgress} 
                                sx={{ height: 8, borderRadius: 4 }}
                            />
                        </Box>
                        <Box sx={{ minWidth: 35 }}>
                            <Typography variant="body2" color="text.secondary">{`${uploadProgress}%`}</Typography>
                        </Box>
                    </Box>
                </Box>
            ) : (
                <>
                    <Box sx={{ 
                        width: 56, height: 56, borderRadius: '50%', 
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mb: 1.5, color: 'primary.main'
                    }}>
                        <CloudUploadIcon fontSize="large" />
                    </Box>
                    <Typography variant="body2" fontWeight="bold" color="text.primary" gutterBottom>
                        {multiple 
                            ? "Drag & drop your files / images here, or click to browse"
                            : "Drag & drop your file here, or click to browse local storage"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {multiple ? `Upload multiple files (Max ${maxSizeMB}MB each)` : `Maximum file size: ${maxSizeMB}MB`}
                    </Typography>
                </>
            )}
        </Box>
    );
}
